import {
  DateValue,
  now as getNow,
  parseAbsoluteToLocal
} from '@internationalized/date';
import { parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo, useState } from 'react';

import {
  RangeValue,
  UseTimeRangeWithTabsProps,
  UseTimeRangeWithTabsReturn
} from './types';
import {
  createTimeRangeFromDateRange,
  createTimeRangeFromOption,
  parseInterval
} from './utils';

export function useTimeRangeWithTabs({
  defaultValue,
  value,
  onChange,
  timeOptions
}: UseTimeRangeWithTabsProps): UseTimeRangeWithTabsReturn {
  const [urlParams] = useQueryStates({
    time: parseAsString.withDefault(defaultValue ?? ''),
    dateFrom: parseAsString.withDefault(''),
    dateTo: parseAsString.withDefault('')
  });

  const urlOption = useMemo(() => urlParams.time, [urlParams.time]);

  const urlDateRange = useMemo(() => {
    if (urlOption !== 'specific' || !urlParams.dateFrom || !urlParams.dateTo) {
      return null;
    }

    try {
      const start = parseAbsoluteToLocal(urlParams.dateFrom);
      const end = parseAbsoluteToLocal(urlParams.dateTo);
      return { start, end };
    } catch {
      return null;
    }
  }, [urlOption, urlParams.dateFrom, urlParams.dateTo]);

  const [internalOption, setInternalOption] = useState<string | null>(() => {
    if (urlOption) return urlOption;
    return defaultValue ?? null;
  });

  const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(
    () => {
      if (urlDateRange) return urlDateRange;
      return null;
    }
  );

  const selectedOption = useMemo(() => {
    if (value) {
      return value.name === 'custom-range' ? null : value.interval;
    }
    return internalOption;
  }, [value, internalOption]);

  const isCustomRange = useMemo(() => {
    if (value) {
      return value.name === 'custom-range';
    }
    return internalOption === null && dateRange !== null;
  }, [value, internalOption, dateRange]);

  const selectOption = useCallback(
    (optionValue: string) => {
      const option = timeOptions.find(opt => opt.value === optionValue);
      const timeRange = createTimeRangeFromOption(optionValue, option);

      setInternalOption(optionValue);
      setDateRange(null);
      onChange?.(timeRange);
    },
    [timeOptions, onChange]
  );

  const selectCustomRange = useCallback(
    (range: RangeValue<DateValue>) => {
      const timeRange = createTimeRangeFromDateRange(range);

      setInternalOption(null);
      setDateRange(range);
      onChange?.(timeRange);
    },
    [onChange]
  );

  const isOptionSelected = useCallback(
    (optionValue: string) => selectedOption === optionValue,
    [selectedOption]
  );

  const getDefaultDateRange = useCallback((): RangeValue<DateValue> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const endDate = getNow(timezone);
    let startDate = getNow(timezone);

    const interval = selectedOption ? parseInterval(selectedOption) : null;

    if (interval) {
      switch (interval.unit) {
        case 'm':
          startDate = startDate.subtract({ minutes: interval.amount });
          break;
        case 'h':
          startDate = startDate.subtract({ hours: interval.amount });
          break;
        case 'd':
          startDate = startDate.subtract({ days: interval.amount });
          break;
        default:
          startDate = startDate.subtract({ hours: 1 });
          break;
      }
    } else {
      startDate = startDate.subtract({ hours: 1 });
    }

    return { start: startDate, end: endDate };
  }, [selectedOption]);

  return {
    selectedOption,
    isCustomRange,
    dateRange,
    selectOption,
    selectCustomRange,
    isOptionSelected,
    getDefaultDateRange
  };
}
